import React from 'react'
import { Label } from '../label/Label'
import { Hint } from '../hint/Hint'

export interface RadioProps {
    /**
     * The classname -- this is mostly used to hook GA event tracking
     */
    className?: string

    /**
     * The name of the input -- this is required to identify the input
     * in the form data.
     * The name will be used as an ID, if ID is not provided.
     */
    name: string

    /**
     * The label elements is useful for assistive technologies such as screen readers.
     */
    label: string

    /**
     * The error message -- a message that is displayed when the input is invalid.
     */
    error?: string | React.ReactNode

    /**
     * The hint -- a helper text to provide more information about the input
     */
    hint?: string

    /**
     * This indicates wheter a input field is required -- this is optional.
     */
    required?: boolean

    /**
     * Disables the input field -- this is optional.
     **/
    disabled?: boolean

    /**
     * Sets the input field to checked -- this is optional.
     * */
    checked?: boolean

    /**
     * This makes it possible to pass through additional props.
     */
    [propName: string]: any
}
// disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200
export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
    (props, ref) => {
        const {
            className = 'js_Radio',
            fontSize,
            label,
            placeholder,
            name,
            error,
            hint,
            required,
            id,
            ...rest
        } = props
        const ariaDescribeRequired = required ? `${name}-required` : ''
        const ariaDescribeError = error ? `${name}-error` : ''
        const ariaDescribeHint = hint ? `${name}-hint` : ''
        const helperClass = error ? 'border-state-error' : 'border-neutral-400'
        const fontSizeClass = fontSize ? fontSize : 'text-base'

        return (
            <div className="relative flex items-start">
                <div className="flex h-5 items-center pl-1">
                    <input
                        type="radio"
                        ref={ref}
                        name={name}
                        id={id || name}
                        {...rest}
                        aria-describedby={`${ariaDescribeRequired} ${ariaDescribeError} ${ariaDescribeHint}`}
                        className={`h-6 w-6 text-black focus:ring-offset-1 disabled:border-neutral-400 disabled:bg-neutral-200 disabled:text-black disabled:hover:border-neutral-400 disabled:hover:bg-neutral-200 ${helperClass} ${className}`}
                    />
                </div>
                <div className="ml-3 text-sm">
                    <Label
                        fontSize={fontSizeClass}
                        htmlFor={id || name}
                        required={required}
                        id={ariaDescribeRequired}
                    >
                        {label}
                    </Label>
                    {hint && <Hint id={ariaDescribeHint}>{hint}</Hint>}
                    {error && (
                        <Hint error id={ariaDescribeError}>
                            {error}
                        </Hint>
                    )}
                </div>
            </div>
        )
    }
)

Radio.displayName = 'Radio'
